import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Clock, LogOut, ShieldCheck, Building2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { usePlatformConfig } from "@/lib/platformConfig";

// Shared hold-screen shell for every blocked state (member or business).
function Hold({ icon: Icon, destructive, title, children }) {
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6" data-testid="approval-gate">
      <div className="w-full max-w-md text-center">
        <div className="w-14 h-14 rounded-full bg-brand-tint flex items-center justify-center mx-auto mb-5">
          <Icon className={`w-7 h-7 ${destructive ? "text-destructive" : "text-brand"}`} />
        </div>
        <h1 className="font-serif text-3xl text-ink mb-2">{title}</h1>
        <div className="text-muted-foreground font-sans leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SignOut({ logout }) {
  return (
    <button data-testid="approval-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-muted-foreground text-sm mt-8 hover:text-ink transition-colors">
      <LogOut className="w-4 h-4" />Sign out
    </button>
  );
}

// Blocks content until a member/business is approved. Business accounts must have a
// payment method on file (payment-first) AND an admin approval before any Business
// features become accessible. Admins always pass.
export default function ApprovalGate({ children }) {
  const { user, logout } = useAuth();
  const { contact } = usePlatformConfig();
  const [paying, setPaying] = useState(false);
  if (!user || user.is_admin) return children;

  // ---- Business accounts: payment-first, then pending admin approval ----
  if (user.account_type === "business") {
    if (user.account_status === "approved") return children;
    if (!user.biz_onboarded) return <Navigate to="/business-onboarding" replace />;
    const rejected = user.account_status === "rejected";

    // Payment-first: no Business features until a card is on file.
    if (!rejected && !user.biz_payment_on_file) {
      const addPayment = async () => {
        setPaying(true);
        try {
          const { url } = await api("/checkout", { method: "POST", body: { kind: "business", business_id: user.user_id } });
          if (url) { window.location.assign(url); return; }
        } catch (e) { toast.error(e.message || "Couldn't start payment"); }
        setPaying(false);
      };
      return (
        <Hold icon={CreditCard} title="One step left, add payment">
          <p data-testid="business-gate-payment">
            Your business account stays locked until a payment method is on file. Add your card to
            send your application for review, you won't be charged until an admin approves your business.
          </p>
          <div className="mt-6 flex flex-col items-center gap-2">
            <button data-testid="business-add-payment-btn" disabled={paying} onClick={addPayment}
              className="inline-flex items-center gap-2 bg-brand text-white font-semibold rounded-pill px-6 py-2.5 transition-opacity disabled:opacity-60">
              <CreditCard className="w-4 h-4" />{paying ? "Starting…" : "Add payment method"}
            </button>
            <SignOut logout={logout} />
          </div>
        </Hold>
      );
    }

    // Paid + pending (or rejected) → held until an admin approves.
    return (
      <Hold icon={rejected ? ShieldCheck : Building2} destructive={rejected}
        title={rejected ? "Business not approved" : "Your business is under review"}>
        <p data-testid="business-gate-review">
          {rejected
            ? `Your business application wasn't approved. If you think this is a mistake, reach out to ${contact.support_email}.`
            : "Thanks, your payment method is on file and your business application has been sent to our team. We review every new business before unlocking Business features, and we'll email you the moment you're approved."}
        </p>
        <SignOut logout={logout} />
      </Hold>
    );
  }

  // ---- Personal members ----
  const blocked = user.account_status === "pending" || user.account_status === "rejected";
  if (!blocked) return children;
  const rejected = user.account_status === "rejected";
  const wantsVerification = user.verification_status === "pending";
  return (
    <Hold icon={rejected ? ShieldCheck : Clock} destructive={rejected}
      title={rejected ? "Application not approved" : "You're on the list"}>
      <p>
        {rejected
          ? `Your request to join Two Degrees wasn't approved. If you think this is a mistake, reach out to ${contact.support_email}.`
          : "Thanks for signing up! Every new member is reviewed by our team before getting access to the community. We'll notify you the moment you're approved."}
      </p>
      {!rejected && wantsVerification && (
        <p className="text-brand text-sm mt-4">You also requested verification, our team will review your references and work.</p>
      )}
      <SignOut logout={logout} />
    </Hold>
  );
}
